/**
 * ProseEntityLinker — make mentions of the run's KNOWN entities (Apex classes,
 * etc.) clickable in the answer prose. We only linkify names the run actually
 * touched (dependencies / impact / artifacts), so there are no false positives,
 * and clicking opens that class in the editor. Plain string scan — no regex.
 */
import { Fragment, type ReactNode } from "react";

/** Identifier char for Apex names (letters, digits, underscore). */
function isIdentChar(ch: string): boolean {
  return (
    (ch >= "A" && ch <= "Z") ||
    (ch >= "a" && ch <= "z") ||
    (ch >= "0" && ch <= "9") ||
    ch === "_"
  );
}

function linkifyString(
  text: string,
  known: Map<string, string | null>,
  onOpen: (name: string, id: string | null) => void,
  keyBase: string,
): ReactNode {
  if (!text) return text;
  const out: ReactNode[] = [];
  let buf = "";
  let i = 0;
  let n = 0;
  const flush = () => {
    if (buf) {
      out.push(buf);
      buf = "";
    }
  };
  while (i < text.length) {
    if (isIdentChar(text[i])) {
      let j = i;
      while (j < text.length && isIdentChar(text[j])) j++;
      const token = text.slice(i, j);
      if (known.has(token)) {
        flush();
        const id = known.get(token) ?? null;
        out.push(
          <button
            key={`${keyBase}-${n++}`}
            type="button"
            onClick={() => onOpen(token, id)}
            title={`Open ${token}`}
            className="ma-entity-link cursor-pointer font-medium text-[hsl(var(--primary))] underline decoration-dotted underline-offset-2 hover:opacity-80"
          >
            {token}
          </button>,
        );
      } else {
        buf += token;
      }
      i = j;
    } else {
      buf += text[i];
      i++;
    }
  }
  flush();
  if (out.length === 0) return text;
  if (out.length === 1 && typeof out[0] === "string") return out[0];
  return out;
}

/**
 * Recursively linkify string children (one level into arrays). Non-string React
 * elements (bold, code, links, the citation chips) are passed through untouched.
 */
export function linkifyEntities(
  children: ReactNode,
  known: Map<string, string | null>,
  onOpen: (name: string, id: string | null) => void,
): ReactNode {
  if (!known || known.size === 0) return children;
  const process = (node: ReactNode, key: string): ReactNode => {
    if (typeof node === "string") return linkifyString(node, known, onOpen, key);
    if (Array.isArray(node)) {
      return node.map((child, idx) => <Fragment key={`${key}.${idx}`}>{process(child, `${key}.${idx}`)}</Fragment>);
    }
    return node;
  };
  return process(children, "ent");
}
